/**
 * AnomalyInbox — actividad sin fichaje.
 *
 * Señales del POS/ERP (Wezamp, TBD) que no casan con el ledger:
 * alguien estuvo trabajando y nadie fichó. Revisión humana, nunca
 * sanción automática.
 */
import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getActivityAnomalies,
  reviewAnomaly,
  ActivityAnomaly,
  apiMessage,
} from '../../services/apiClient';

export function AnomalyInbox() {
  const [anomalies, setAnomalies] = useState<ActivityAnomaly[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setAnomalies(await getActivityAnomalies());
  }, []);

  useEffect(() => {
    load().catch(() => setError('Error cargando anomalías'));
  }, [load]);

  // Sin captura, un 401 de sesión caducada o un 5xx dejaba el botón muerto en
  // silencio: la anomalía seguía igual y el manager no sabía que su clic
  // fracasó (ronda 3, r2b20[4]) — el mismo silencio que TimeCorrectionInbox
  // documentó haber arreglado. El mensaje se pega a la tarjeta que falló.
  const [reviewError, setReviewError] = useState<{ id: number; message: string } | null>(null);

  const review = async (id: number) => {
    setReviewError(null);
    try {
      await reviewAnomaly(id);
      await load();
    } catch (e: unknown) {
      setReviewError({ id, message: apiMessage(e, 'No se pudo marcar la anomalía como revisada') });
    }
  };

  return (
    <AdminLayout title="Actividad sin fichaje">
      <div className="p-4 space-y-3">
        <p className="text-xs text-ink-2">
          Señales de actividad (POS, imputaciones) sin jornada abierta en el
          registro. Ni crean fichajes ni sancionan: pide la corrección al
          trabajador desde Correcciones.
        </p>
        {error && (
          <div className="bg-danger-soft text-danger rounded-lg p-3 text-sm">
            {error}
          </div>
        )}

        {anomalies.map((a) => (
          <div
            key={a.id}
            className="bg-surface rounded-xl p-4 shadow-sm flex items-center justify-between"
          >
            <div>
              <p className="font-medium text-ink text-sm">
                {a.user_name ?? `Usuario #${a.user_id}`} · fuente: {a.source}
              </p>
              <p className="text-xs text-ink-2">
                {new Date(a.started_at).toLocaleString('es-ES')}
                {a.ended_at && ` → ${new Date(a.ended_at).toLocaleTimeString('es-ES')}`}
                {a.worksite_code && ` · ${a.worksite_code}`}
              </p>
            </div>
            <div className="text-right">
              {reviewError?.id === a.id && (
                <p className="text-[12px] text-danger mb-1.5 max-w-[220px]">{reviewError.message}</p>
              )}
              <button
                onClick={() => review(a.id)}
                className="px-3 py-2 bg-ink-2 text-white rounded-lg text-sm"
              >
                Revisada
              </button>
            </div>
          </div>
        ))}

        {anomalies.length === 0 && (
          <p className="text-center text-sm text-ink-3 py-10">
            Sin anomalías pendientes ✓
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
